<script setup lang="ts">
import useUserStore from '~~/app/store/user'
import usePoolStore from '~~/app/store/pool'
import SectionInfosDescription from '~~/app/components/Pools/SectionInfosDescription.vue'
import SectionInfosCoveredArea from '~~/app/components/Pools/SectionInfosCoveredArea.vue'
import SectionInfosNetwork from '~~/app/components/Pools/SectionInfosNetwork.vue'
import SectionInfosCompatibilityAndRating from '~~/app/components/Pools/SectionInfosCompatibilityAndRating.vue'
import SectionInfosAddress from '~~/app/components/Pools/SectionInfosAddress.vue'
import SectionInfosSeparator from '~~/app/components/Pools/SectionInfosSeparator.vue'
import ServiceMessage from '~~/app/components/Pools/ServiceMessage.vue'
import { ServiceMessageType } from '~~/interfaces/UI'
import PoolConnectorsList from './PoolConnectorsList.vue'

defineProps({
    isContainedInDrawer: {
        type: Boolean,
        default: true
    },
    isExploitation: {
        type: Boolean,
        default: false
    },
    isPartner: {
        type: Boolean,
        default: false
    }
})

const emits = defineEmits([
    'showPricesInDrawer',
    'showPricesInPopup',
    'updateSection',
    'showReviewPopUp',
    'showGlobalScoreAverage'
])

const userStore = useUserStore()
const poolStore = usePoolStore()

const discountMessageKey = computed<string>(
    () =>
        `discounted_networks.service_message.${userStore.userAlreadyHasAPass ? 'has_pass' : 'has_no_pass'}`
)

const serviceMessageStyle = computed<ServiceMessageType>(() =>
    userStore.userAlreadyHasAPass
        ? ServiceMessageType.Positive
        : ServiceMessageType.Info
)

const redirectToChargemap = (path: string) => {
    if (window.location !== window?.parent?.location) {
        ;(window.top as Window).postMessage(
            { action: 'openUrl', url: path },
            document.referrer
        )
    } else {
        return navigateTo(getChargemapUrl(path), { external: true })
    }
}
</script>

<template>
    <div class="py-4 px-4 bg-white">
        <DiscoverChargemapPass
            v-if="
                !isExploitation && !userStore.userAlreadyHasAPass && !isPartner
            "
            class="mb-5"
            :compatible="poolStore.canChargeWithChargemapPass"
            :drawer="isContainedInDrawer"
            @click="(path: string) => redirectToChargemap(path)"
        />
        <PoolChargemapCompatibility
            v-if="!isPartner"
            class="pb-5"
            :is-compatible="poolStore.canChargeWithChargemapPass"
            :is-remote-compatible="poolStore.isRemoteCompatible"
            @showPrices="emits('showPricesInDrawer', true)"
        />
        <ServiceMessage
            v-if="poolStore.hasActiveDiscount"
            icon-name="IconGift"
            :text-key="discountMessageKey"
            :type="serviceMessageStyle"
            display-order-pass
        />
        <ChargemapB2CButton
            v-if="isPartner"
            type="primary"
            custom="full-width"
            class="mb-5"
            @click="emits('showPricesInDrawer', true)"
        >
            {{ $t('pool.see_prices') }}
        </ChargemapB2CButton>
        <SectionInfosCompatibilityAndRating
            v-if="parseFloat(poolStore.getRating) !== 0"
            :is-contained-in-drawer
            @showGlobalScoreAverage="emits('showGlobalScoreAverage')"
            @updateSection="emits('updateSection', 'reviews')"
            @showReviewPopUp="emits('showReviewPopUp', true)"
        />
        <div v-if="isContainedInDrawer">
            <SectionInfosSeparator class="mb-2" />
            <PoolConnectorsList
                class="cursor-pointer"
                @click="emits('updateSection', 'stations')"
            />
            <SectionInfosSeparator class="mt-2 mb-4" />
            <SectionInfosAddress :is-contained-in-drawer />
        </div>
        <div v-else>
            <SectionInfosSeparator
                v-if="parseFloat(poolStore.getRating) !== 0"
                class="my-3"
            />
            <SectionInfosAddress :is-contained-in-drawer />
        </div>
        <SectionInfosSeparator class="my-4" />
        <SectionInfosNetwork v-if="poolStore.getNetworkName" />
        <SectionInfosCoveredArea />
        <SectionInfosSeparator
            v-if="poolStore.getDescription && !isContainedInDrawer"
            class="my-4"
        />
        <SectionInfosDescription v-if="!isContainedInDrawer" />
    </div>
</template>
